/**
 * Browser client for /api/portal/*. The request never names who is asking:
 * the server reads the sign-in from the validated token. See api/src/shared/sewerPortal.ts.
 */
import { getApiTokenSilent } from "../../shell/auth/tokens";
import { apiAuthHeaders } from "../../lib/apiAuthHeaders";

export interface IPortalWipRow {
  wipId: string; sku: string; qty: number; ds?: string; grn?: string;
  received: number; passed: number; failed: number; paid: number;
}
export interface IPortalDsRow { dsNo: string; issuedAt: string; wipIds: string[] }
export interface IPortalPayoutRow { ref: string; periodEnd: string; total: number; sets: number; status: string; doc?: unknown }
export interface IPortalPerfRow { grade: string; firstPassPct?: number; onTimePct?: number; period?: string }
export interface IPortalDecl {
  id: number; sewerKey: string; wipId: string; sets: number; status: "open" | "received" | "withdrawn";
  note: string; declaredBy: string; declaredAt: string; updatedAt: string; receivedSets: number | null; drNo?: string | null;
}
export interface IPortalHome {
  displayName?: string; sewerKey: string; preview?: boolean; publishedAt: string | null;
  snapshot: { ds: IPortalDsRow[]; wips: IPortalWipRow[]; payouts: IPortalPayoutRow[]; perf?: IPortalPerfRow };
  drs?: { drNo: string; status: string; submittedAt: string; receivedAt: string | null; shortReason?: string | null }[]; declarations: IPortalDecl[]; acked: { dsNo: string; at: string; sets?: number | null }[];
}
export interface IPortalAccount { email: string; sewerKey: string; displayName: string; active: boolean; publishedAt: string | null }
export interface IPortalMe { mapped: boolean; staff: boolean; sewerKey?: string; displayName?: string }

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const token = await getApiTokenSilent();
  if (!token) throw new Error("Sign in again to continue.");
  const res = await fetch(url, {
    ...init,
    headers: { ...apiAuthHeaders(token), ...(init?.body ? { "Content-Type": "application/json" } : {}) },
  });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status}).`);
  return body;
}
const post = <T,>(url: string, body: unknown) => call<T>(url, { method: "POST", body: JSON.stringify(body) });

export const portalMe = () => call<IPortalMe>("/api/portal/me");
export const portalHome = (as?: string) => call<IPortalHome>(`/api/portal/home${as ? `?as=${encodeURIComponent(as)}` : ""}`);
export const portalDeclare = (b: { wipId: string; sets: number; id?: number; note?: string }) => post<{ ok: true }>("/api/portal/declare", b);
export const portalAck = (dsNo: string) => post<{ ok: true }>("/api/portal/ack", { dsNo });
export const portalAckWip = (wipId: string, sets: number) => post<{ ok: true }>("/api/portal/ack", { wipId, sets });
export const portalPublishOne = (sewerKey: string, snapshot: unknown) => post<{ ok: true }>("/api/portal/publish", { sewerKey, snapshot });
export const portalAccounts = () => call<{ accounts: IPortalAccount[] }>("/api/portal/accounts");
export const portalSaveAccount = (a: { email: string; sewerKey: string; displayName: string; active: boolean }) =>
  post<{ accounts: IPortalAccount[] }>("/api/portal/accounts", a);
export const portalRemoveAccount = (email: string) =>
  post<{ accounts: IPortalAccount[] }>("/api/portal/accounts", { email, remove: true });
export const portalDeclarations = () => call<{ declarations: IPortalDecl[] }>("/api/portal/declarations");
export const portalResolve = (id: number, receivedSets: number) => post<{ ok: true }>("/api/portal/resolve", { id, receivedSets });

export interface IDrLookup { drNo: string; sewerKey: string; displayName: string; status: string; submittedAt: string; shortReason?: string | null; lines: IPortalDecl[] }
export const portalSubmitDr = (drNo: string) => post<{ ok: true }>("/api/portal/dr/submit", { drNo });
export const portalDrLookup = (no: string) => call<IDrLookup>(`/api/portal/dr/lookup?no=${encodeURIComponent(no)}`);
export const portalDrReceive = (drNo: string, lines: { id: number; receivedSets: number }[], shortReason?: string) => post<{ ok: true }>("/api/portal/dr/receive", { drNo, lines, shortReason });
export interface IDrLogRow { id: number; sewerKey: string; drNo: string; action: string; by: string; reason: string; at: string }
export const portalDrLog = (no?: string) => call<{ log: IDrLogRow[] }>(`/api/portal/dr/log${no ? `?no=${encodeURIComponent(no)}` : ""}`);
export const portalDrReopen = (drNo: string, reason: string) => post<{ ok: true }>("/api/portal/dr/reopen", { drNo, reason });
